import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";
import { DestinatariosEditor, destinatarioDesdeApi, destinatariosParaGuardar } from "@/components/admin/DestinatariosEditor";

const MAX_DESTINATARIOS_UNIDAD = 5;

const VACIO = {
  vin: "", tipo: "", marca: "", modelo: "", anio: "", color: "",
  cliente_id: "", datos_envio: {
    origen: "", destino: "", puerto_origen: "", puerto_destino: "", buque: "",
    booking: "", pedido_cliente: "", numero_factura: "", fecha_estimada_entrega: "",
  },
  destinatarios_override: [],
};

export default function UnidadForm() {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  usePageTitle(esEdicion ? "Editar unidad" : "Nueva unidad");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(VACIO);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const { data: clientes = [] } = useQuery({
    queryKey: ["admin-clientes-opciones"],
    queryFn: () => adminApi.get("/admin/clientes/opciones").then((r) => r.data),
  });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => adminApi.get("/tipos").then((r) => r.data),
  });

  const {
    data: unidadData, isLoading: cargandoUnidad, isError: errorUnidad, refetch: refetchUnidad,
  } = useQuery({
    queryKey: ["admin-unidad", id],
    queryFn: () => adminApi.get(`/admin/unidades/${id}`).then((r) => r.data),
    enabled: esEdicion,
  });

  useEffect(() => {
    if (!unidadData) return;
    const u = unidadData.unidad;
    setForm({
      vin: u.vin, tipo: u.tipo, marca: u.marca, modelo: u.modelo, anio: u.anio,
      color: u.color, cliente_id: u.cliente_id,
      datos_envio: {
        origen: u.datos_envio?.origen || "", destino: u.datos_envio?.destino || "",
        puerto_origen: u.datos_envio?.puerto_origen || "", puerto_destino: u.datos_envio?.puerto_destino || "",
        buque: u.datos_envio?.buque || "", booking: u.datos_envio?.booking || "",
        pedido_cliente: u.datos_envio?.pedido_cliente || "",
        numero_factura: u.datos_envio?.numero_factura || "",
        fecha_estimada_entrega: u.datos_envio?.fecha_estimada_entrega?.slice(0, 10) || "",
      },
      destinatarios_override: u.destinatarios_override?.length ? u.destinatarios_override.map(destinatarioDesdeApi) : [],
    });
  }, [unidadData]);

  const upd = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const updEnvio = (campo, valor) => setForm((f) => ({ ...f, datos_envio: { ...f.datos_envio, [campo]: valor } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const body = {
      ...form, vin: form.vin.trim().toUpperCase(),
      // Fix H (2026-09-14): año y color son opcionales. Vacío viaja como null, no como 0
      // ni como "": Number("") da 0 y habría guardado el año 0 en vez de "sin dato".
      anio: String(form.anio).trim() ? Number(form.anio) : null,
      color: (form.color || "").trim() || null,
      destinatarios_override: destinatariosParaGuardar(form.destinatarios_override),
    };
    try {
      if (esEdicion) {
        await adminApi.put(`/admin/unidades/${id}`, body);
        queryClient.invalidateQueries({ queryKey: ["admin-unidad", id] });
        queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
        navigate(`/admin/unidades/${id}`);
      } else {
        const { data } = await adminApi.post("/admin/unidades", body);
        queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
        queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
        navigate(`/admin/unidades/${data.id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  if (esEdicion && cargandoUnidad) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <Loader2 className="animate-spin text-[var(--tc-accent)]" size={26} />
      </div>
    );
  }

  if (esEdicion && errorUnidad) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <ErrorState testId="unidad-form-error" mensaje="No se pudo cargar la unidad para editar." onRetry={refetchUnidad} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <button
        onClick={() => navigate(-1)}
        className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:text-[var(--tc-accent)]"
      >
        <ArrowLeft size={15} /> Volver
      </button>

      <h1 className="text-xl font-semibold text-[var(--tc-text)]">{esEdicion ? "Editar unidad" : "Nueva unidad"}</h1>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-6">
        <Seccion titulo="Datos de la unidad">
          <Campo label="VIN (17 caracteres, sin I/O/Q)" className="sm:col-span-2">
            <input
              data-testid={ADMIN.unidadFormVin}
              required maxLength={17}
              value={form.vin}
              onChange={(e) => upd("vin", e.target.value.toUpperCase())}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 font-mono text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </Campo>
          <Campo label="Tipo">
            <select
              data-testid={ADMIN.unidadFormTipo}
              required
              value={form.tipo}
              onChange={(e) => upd("tipo", e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            >
              <option value="">Seleccioná uno</option>
              {tipos.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Cliente corporativo">
            <select
              data-testid={ADMIN.unidadFormCliente}
              required
              value={form.cliente_id}
              onChange={(e) => upd("cliente_id", e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            >
              <option value="">Seleccioná uno</option>
              {clientes.filter((c) => c.activo || c.id === form.cliente_id).map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Marca">
            <input data-testid={ADMIN.unidadFormMarca} required value={form.marca} onChange={(e) => upd("marca", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </Campo>
          <Campo label="Modelo">
            <input data-testid={ADMIN.unidadFormModelo} required value={form.modelo} onChange={(e) => upd("modelo", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </Campo>
          <Campo label="Año (opcional)">
            <input data-testid={ADMIN.unidadFormAnio} type="number" placeholder="Se puede completar después" value={form.anio ?? ""} onChange={(e) => upd("anio", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </Campo>
          <Campo label="Color (opcional)">
            <input data-testid={ADMIN.unidadFormColor} placeholder="Se puede completar después" value={form.color ?? ""} onChange={(e) => upd("color", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </Campo>
        </Seccion>

        <Seccion titulo="Datos del envío">
          <Campo label="Origen"><input data-testid={ADMIN.unidadFormOrigen} value={form.datos_envio.origen} onChange={(e) => updEnvio("origen", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Destino"><input data-testid={ADMIN.unidadFormDestino} value={form.datos_envio.destino} onChange={(e) => updEnvio("destino", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Puerto de origen"><input data-testid={ADMIN.unidadFormPuertoOrigen} value={form.datos_envio.puerto_origen} onChange={(e) => updEnvio("puerto_origen", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Puerto de destino"><input data-testid={ADMIN.unidadFormPuertoDestino} value={form.datos_envio.puerto_destino} onChange={(e) => updEnvio("puerto_destino", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Buque"><input data-testid={ADMIN.unidadFormBuque} value={form.datos_envio.buque} onChange={(e) => updEnvio("buque", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Booking"><input data-testid={ADMIN.unidadFormBooking} value={form.datos_envio.booking} onChange={(e) => updEnvio("booking", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Pedido del cliente"><input data-testid={ADMIN.unidadFormPedidoCliente} value={form.datos_envio.pedido_cliente} onChange={(e) => updEnvio("pedido_cliente", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="N° de factura"><input data-testid={ADMIN.unidadFormFactura} value={form.datos_envio.numero_factura} onChange={(e) => updEnvio("numero_factura", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
          <Campo label="Entrega estimada"><input data-testid={ADMIN.unidadFormFechaEntrega} type="date" value={form.datos_envio.fecha_estimada_entrega} onChange={(e) => updEnvio("fecha_estimada_entrega", e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" /></Campo>
        </Seccion>

        <Seccion titulo="Notificación de esta unidad (opcional)">
          <p className="text-xs leading-relaxed text-[var(--tc-text-secondary)] sm:col-span-2">
            Si cargás destinatarios acá (ej. el comprador final), REEMPLAZAN a los del cliente
            corporativo para esta unidad. Hasta {MAX_DESTINATARIOS_UNIDAD}.
          </p>
          <div className="sm:col-span-2">
            <DestinatariosEditor
              destinatarios={form.destinatarios_override}
              onChange={(v) => upd("destinatarios_override", v)}
              max={MAX_DESTINATARIOS_UNIDAD}
            />
          </div>
        </Seccion>

        {error && (
          <p data-testid={ADMIN.unidadFormError} className="rounded-[var(--tc-radius)] bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            data-testid={ADMIN.unidadFormSubmit}
            type="submit"
            disabled={guardando}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:opacity-60"
          >
            {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {esEdicion ? "Guardar cambios" : "Crear unidad"}
          </button>
          <button
            data-testid={ADMIN.unidadFormCancel}
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-5 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)]"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6">
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">{titulo}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Campo({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">{label}</label>
      {children}
    </div>
  );
}
